import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faPlus, 
  faEdit, 
  faTrash, 
  faPlay,
  faServer,
  faCalendar,
  faClock,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminEpisodes.css';

const AdminEpisodes = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [showServerModal, setShowServerModal] = useState(false);

  // API endpoints
  const API_BASE = 'https://phimapi.com/v1/api';
  const CDN_IMAGE = 'https://phimimg.com/';

  // Default servers
  const defaultServers = [
    { id: 1, name: 'Server VIP', url: 'https://vip.example.com', priority: 1, status: 'active' },
    { id: 2, name: 'Server HD', url: 'https://hd.example.com', priority: 2, status: 'active' },
    { id: 3, name: 'Server FB', url: 'https://fb.example.com', priority: 3, status: 'active' }
  ];



  useEffect(() => {
    fetchMovieAndEpisodes();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMovieAndEpisodes = async () => {
    setLoading(true);
    try {
      // Fetch movie details from API
      console.log('Fetching movie with slug:', slug);
      const movieResponse = await fetch(`${API_BASE}/phim/${slug}`);
      const movieData = await movieResponse.json();
      console.log('Movie API response:', movieData);
      
      if (movieData?.status === true && movieData.data?.item) {
        const movieInfo = movieData.data.item;
        
        setMovie({
          id: movieInfo._id || slug,
          name: movieInfo.name,
          origin_name: movieInfo.origin_name,
          slug: movieInfo.slug,
          description: movieInfo.content || 'Chưa có mô tả',
          poster_url: movieInfo.poster_url?.replace('https://phimimg.com/', '') || movieInfo.thumb_url?.replace('https://phimimg.com/', ''),
          thumb_url: movieInfo.thumb_url?.replace('https://phimimg.com/', ''),
          total_episodes: parseInt(movieInfo.episode_total) || 0,
          current_episodes: parseInt(movieInfo.episode_current?.replace(/\D/g, '')) || 0,
          status: movieInfo.episode_current === movieInfo.episode_total && movieInfo.episode_total > 0 ? 'completed' : 'ongoing',
          year: movieInfo.year,
          quality: movieInfo.quality,
          lang: movieInfo.lang,
          country: movieInfo.country?.[0]?.name || 'Nhật Bản',
          categories: movieInfo.category || []
        });

        // Process episodes from API
        if (movieInfo.episodes && movieInfo.episodes.length > 0) {
          const allEpisodes = [];
          
          movieInfo.episodes.forEach((serverGroup) => {
            if (serverGroup.server_data && serverGroup.server_data.length > 0) {
              serverGroup.server_data.forEach((episode, index) => {
                const episodeNumber = index + 1;
                
                // Check if episode already exists (avoid duplicates)
                let existingEpisode = allEpisodes.find(ep => ep.episode_number === episodeNumber);
                
                if (!existingEpisode) {
                  existingEpisode = {
                    id: `${slug}-ep-${episodeNumber}`,
                    episode_number: episodeNumber,
                    name: episode.name || `Tập ${episodeNumber}`,
                    slug: episode.slug,
                    filename: episode.filename,
                    link_embed: episode.link_embed,
                    link_m3u8: episode.link_m3u8,
                    duration: '24:00', // Default duration
                    views: Math.floor(Math.random() * 100000) + 10000,
                    air_date: new Date().toISOString().split('T')[0],
                    servers: []
                  };
                  allEpisodes.push(existingEpisode);
                }
                
                // Add server info to episode
                if (!existingEpisode.servers.find(s => s.server_name === serverGroup.server_name)) {
                  existingEpisode.servers.push({
                    server_id: existingEpisode.servers.length + 1,
                    server_name: serverGroup.server_name,
                    url: episode.link_embed || episode.link_m3u8,
                    quality: 'HD'
                  });
                }
              });
            }
          });
          
          // Sort episodes by episode number
          allEpisodes.sort((a, b) => a.episode_number - b.episode_number);
          setEpisodes(allEpisodes);
        } else {
          setEpisodes([]);
        }
        
        // Set default servers
        setServers(defaultServers);
      } else {
        // Fallback if movie not found in API - create demo data for testing
        console.log('Movie not found, API message:', movieData?.msg);
        console.log('Creating fallback movie data for slug:', slug);
        
        // Create demo movie data based on slug
        setMovie({
          id: slug,
          name: `Demo Movie: ${slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
          origin_name: 'Demo Original Name',
          poster_url: '/logo.png', // Use fallback image
          total_episodes: 3,
          current_episodes: 3,
          status: 'demo'
        });
        
        // Create demo episodes
        const demoEpisodes = [
          {
            id: `${slug}-ep-1`,
            episode_number: 1,
            name: 'Tập 1: Demo Episode',
            slug: `${slug}-tap-1`,
            duration: '24:00',
            views: 125000,
            air_date: '2024-01-15',
            servers: [
              { server_id: 1, server_name: 'Server VIP', url: 'https://demo.com/ep1', quality: 'HD' },
              { server_id: 2, server_name: 'Server HD', url: 'https://demo.com/ep1', quality: 'HD' }
            ]
          },
          {
            id: `${slug}-ep-2`,
            episode_number: 2,
            name: 'Tập 2: Demo Episode 2',
            slug: `${slug}-tap-2`,
            duration: '23:45',
            views: 98000,
            air_date: '2024-01-22',
            servers: [
              { server_id: 1, server_name: 'Server VIP', url: 'https://demo.com/ep2', quality: 'HD' }
            ]
          },
          {
            id: `${slug}-ep-3`,
            episode_number: 3,
            name: 'Tập 3: Demo Episode 3',
            slug: `${slug}-tap-3`,
            duration: '24:15',
            views: 87000,
            air_date: '2024-01-29',
            servers: [
              { server_id: 1, server_name: 'Server VIP', url: 'https://demo.com/ep3', quality: 'HD' },
              { server_id: 3, server_name: 'Server FB', url: 'https://demo.com/ep3', quality: 'SD' }
            ]
          }
        ];
        
        setEpisodes(demoEpisodes);
        setServers(defaultServers);
      }
    } catch (error) {
      console.error('Error fetching movie and episodes:', error);
      // Fallback data
      setMovie({
        id: slug,
        name: `Lỗi tải dữ liệu cho: ${slug}`,
        origin_name: '',
        poster_url: '/logo.png',
        total_episodes: 0,
        current_episodes: 0,
        status: 'error'
      });
      setEpisodes([]);
      setServers(defaultServers);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEpisode = () => {
    setEditingEpisode(null);
    setShowAddModal(true);
  };

  const handleEditEpisode = (episode) => {
    setEditingEpisode(episode);
    setShowAddModal(true);
  };

  const handleDeleteEpisode = (episodeId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa tập phim này?')) {
      const updatedEpisodes = episodes.filter(ep => ep.id !== episodeId);
      setEpisodes(updatedEpisodes);
    }
  };

  const handleSaveEpisode = (episodeData) => {
    if (editingEpisode) {
      // Update existing episode
      const updatedEpisodes = episodes.map(ep => 
        ep.id === editingEpisode.id ? episodeData : ep
      );
      setEpisodes(updatedEpisodes);
    } else {
      // Add new episode
      const newEpisode = {
        ...episodeData,
        id: Date.now(),
        views: 0
      };
      setEpisodes([...episodes, newEpisode]);
    }
    setShowAddModal(false);
    setEditingEpisode(null);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getServerName = (serverId) => {
    const server = servers.find(s => s.id === serverId);
    return server ? server.name : 'Unknown Server';
  };

  if (loading) {
    return (
      <div className="admin-episodes">
        <div className="episodes-loading">
          <div className="loading-spinner"></div>
          <p>Đang tải danh sách tập phim...</p>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="admin-episodes">
        <div className="episodes-header">
          <div className="header-top">
            <button className="back-btn" onClick={() => navigate('/admin/movies')}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Quay lại danh sách phim
            </button>
          </div>
          <div className="movie-info">
            <div className="movie-poster">
              <img src="/logo.png" alt="Not found" />
            </div>
            <div className="movie-details">
              <h2>Lỗi tải phim</h2>
              <p>Không thể tải thông tin phim</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getPosterUrl = () => {
    if (!movie.poster_url) return '/logo.png';
    if (movie.poster_url.startsWith('/logo.png')) return movie.poster_url;
    return `${CDN_IMAGE}${movie.poster_url}`;
  };

  return (
    <div className="admin-episodes">
      <div className="episodes-header">
        <div className="header-top">
          <button className="back-btn" onClick={() => navigate('/admin/movies')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Quay lại danh sách phim
          </button>
        </div>
        
        <div className="movie-info">
          <div className="movie-poster">
            <img 
              src={getPosterUrl()} 
              alt={movie.name || 'Movie poster'}
              onError={(e) => {
                e.target.src = '/logo.png';
              }}
            />
          </div>
          <div className="movie-details">
            <h2>{movie.name || 'Không có tên'}</h2>
            <p className="movie-origin-name">{movie.origin_name || ''}</p>
            {movie.description && movie.description !== 'Chưa có mô tả' && (
              <div className="movie-description">
                <p>{movie.description}</p>
              </div>
            )}
            <div className="episode-stats">
              <span>{movie.current_episodes}/{movie.total_episodes} tập</span>
              <span>{formatNumber(episodes.reduce((sum, ep) => sum + ep.views, 0))} lượt xem</span>
              <span>Năm: {movie.year}</span>
              <span>{movie.quality} • {movie.lang}</span>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <button 
            className="manage-servers-btn"
            onClick={() => navigate('/admin/servers')}
          >
            <FontAwesomeIcon icon={faServer} />
            Quản lý Server
          </button>
          <button className="add-episode-btn" onClick={handleAddEpisode}>
            <FontAwesomeIcon icon={faPlus} />
            Thêm tập mới
          </button>
        </div>
      </div>

      {/* Episodes List */}
      <div className="episodes-list">
        {episodes.length === 0 ? (
          <div className="no-episodes">
            <FontAwesomeIcon icon={faPlay} />
            <h3>Chưa có tập phim nào</h3>
            <p>Hãy thêm tập phim đầu tiên cho bộ phim này</p>
            <button className="add-first-episode-btn" onClick={handleAddEpisode}>
              <FontAwesomeIcon icon={faPlus} />
              Thêm tập đầu tiên
            </button>
          </div>
        ) : (
          <div className="episodes-grid">
            {episodes
              .sort((a, b) => a.episode_number - b.episode_number)
              .map(episode => (
                <div key={episode.id} className="episode-card">
                  <div className="episode-header">
                    <div className="episode-number">Tập {episode.episode_number}</div>
                    <div className="episode-actions">
                      <button 
                        className="action-btn view"
                        title="Xem tập phim"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button 
                        className="action-btn edit"
                        title="Chỉnh sửa"
                        onClick={() => handleEditEpisode(episode)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="action-btn delete"
                        title="Xóa"
                        onClick={() => handleDeleteEpisode(episode.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="episode-content">
                    <h3 className="episode-name">{episode.name}</h3>
                    
                    <div className="episode-meta">
                      <div className="meta-item">
                        <FontAwesomeIcon icon={faClock} />
                        <span>{episode.duration}</span>
                      </div>
                      <div className="meta-item">
                        <FontAwesomeIcon icon={faCalendar} />
                        <span>{new Date(episode.air_date).toLocaleDateString('vi-VN')}</span>
                      </div>
                      <div className="meta-item">
                        <FontAwesomeIcon icon={faEye} />
                        <span>{formatNumber(episode.views)}</span>
                      </div>
                    </div>
                    
                    <div className="episode-servers">
                      <div className="servers-label">Servers:</div>
                      <div className="servers-list">
                        {episode.servers.map((server, index) => (
                          <div key={index} className="server-badge">
                            <FontAwesomeIcon icon={faServer} />
                            <span>{getServerName(server.server_id)}</span>
                            <span className="quality">{server.quality}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>

      {/* Add/Edit Episode Modal */}
      {showAddModal && (
        <EpisodeForm
          episode={editingEpisode}
          servers={servers}
          onSave={handleSaveEpisode}
          onCancel={() => setShowAddModal(false)}
        />
      )}

      {/* Server Management Modal */}
      {showServerModal && (
        <ServerManagement
          servers={servers}
          onServersUpdate={setServers}
          onClose={() => setShowServerModal(false)}
        />
      )}
    </div>
  );
};

// Episode Form Component
const EpisodeForm = ({ episode, servers, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    episode_number: '',
    name: '',
    slug: '',
    duration: '',
    air_date: '',
    servers: []
  });

  useEffect(() => {
    if (episode) {
      setFormData(episode);
    } else {
      setFormData({
        episode_number: '',
        name: '',
        slug: '',
        duration: '',
        air_date: new Date().toISOString().split('T')[0],
        servers: []
      });
    }
  }, [episode]);

  // Auto-generate slug from name
  useEffect(() => {
    if (formData.name && !episode) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name, episode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServerChange = (serverId, field, value) => {
    const updatedServers = formData.servers.map(server => 
      server.server_id === serverId 
        ? { ...server, [field]: value }
        : server
    );
    setFormData(prev => ({ ...prev, servers: updatedServers }));
  };

  const handleAddServer = (serverId) => {
    if (!formData.servers.find(s => s.server_id === serverId)) {
      const newServer = {
        server_id: serverId,
        url: '',
        quality: 'HD'
      };
      setFormData(prev => ({
        ...prev,
        servers: [...prev.servers, newServer]
      }));
    }
  };

  const handleRemoveServer = (serverId) => {
    const updatedServers = formData.servers.filter(s => s.server_id !== serverId);
    setFormData(prev => ({ ...prev, servers: updatedServers }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.episode_number) {
      alert('Vui lòng nhập đầy đủ thông tin tập phim');
      return;
    }

    if (formData.servers.length === 0) {
      alert('Vui lòng thêm ít nhất một server');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content episode-form-modal">
        <div className="modal-header">
          <h3>{episode ? 'Chỉnh sửa tập phim' : 'Thêm tập phim mới'}</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Số tập *</label>
                <input
                  type="number"
                  value={formData.episode_number}
                  onChange={(e) => handleInputChange('episode_number', parseInt(e.target.value))}
                  min="1"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Thời lượng</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  placeholder="24:30"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Tên tập *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Tên tập phim"
                  required
                />
              </div>
              
              <div className="form-group full-width">
                <label>Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  placeholder="URL-friendly slug"
                />
              </div>
              
              <div className="form-group">
                <label>Ngày phát sóng</label>
                <input
                  type="date"
                  value={formData.air_date}
                  onChange={(e) => handleInputChange('air_date', e.target.value)}
                />
              </div>
            </div>
            
            {/* Servers Section */}
            <div className="servers-section">
              <h4>Server & Links</h4>
              
              <div className="available-servers">
                <label>Thêm server:</label>
                <div className="server-buttons">
                  {servers.filter(server => server.status === 'active').map(server => (
                    <button
                      key={server.id}
                      type="button"
                      className={`server-btn ${formData.servers.find(s => s.server_id === server.id) ? 'added' : ''}`}
                      onClick={() => handleAddServer(server.id)}
                      disabled={formData.servers.find(s => s.server_id === server.id)}
                    >
                      <FontAwesomeIcon icon={faServer} />
                      {server.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="episode-servers-config">
                {formData.servers.map(server => {
                  const serverInfo = servers.find(s => s.id === server.server_id);
                  return (
                    <div key={server.server_id} className="server-config">
                      <div className="server-config-header">
                        <span className="server-name">
                          <FontAwesomeIcon icon={faServer} />
                          {serverInfo?.name}
                        </span>
                        <button
                          type="button"
                          className="remove-server-btn"
                          onClick={() => handleRemoveServer(server.server_id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      
                      <div className="server-config-form">
                        <div className="form-group">
                          <label>URL Video *</label>
                          <input
                            type="url"
                            value={server.url}
                            onChange={(e) => handleServerChange(server.server_id, 'url', e.target.value)}
                            placeholder="https://example.com/video.m3u8"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Chất lượng</label>
                          <select
                            value={server.quality}
                            onChange={(e) => handleServerChange(server.server_id, 'quality', e.target.value)}
                          >
                            <option value="HD">HD</option>
                            <option value="Full HD">Full HD</option>
                            <option value="4K">4K</option>
                            <option value="SD">SD</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Hủy
            </button>
            <button type="submit" className="btn-primary">
              {episode ? 'Cập nhật' : 'Thêm tập'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Server Management Component (placeholder)
const ServerManagement = ({ servers, onServersUpdate, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Quản lý Server</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <p>Chức năng quản lý server sẽ được implement ở bước tiếp theo...</p>
          <div className="servers-list">
            {servers.map(server => (
              <div key={server.id} className="server-item">
                <span>{server.name}</span>
                <span className={`status ${server.status}`}>{server.status}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Đóng</button>
        </div>
      </div>
    </div>
  );
};

export default AdminEpisodes;